import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import { useTranslation } from 'react-i18next';

export type GeneralDataCardProps = {
  title: string;
  value: number | undefined | null;
  change: number;
  changeLabel: string;
  prefix?: string;
  suffix?: string;
  multiplier?: number;
  isLoading: boolean;
  index: number;
};

export const GeneralDataCard = ({
  title,
  value,
  change,
  changeLabel,
  prefix,
  suffix,
  multiplier,
  isLoading,
  index,
}: GeneralDataCardProps) => {
  const isPositive = change > 0;
  const isNegative = change < 0;
  const noChange = change === 0;
  const { t } = useTranslation();
  const isValueAvailable = value !== null && value !== undefined;
  const displayMultiplier = multiplier ?? (suffix === '%' ? 100 : 1);

  return (
    <Card
      data-test="general-data-card"
      sx={{
        padding: theme => theme.spacing(1.5),
        width: '144px',
        height: '112px',
        display: 'flex',
        flexDirection: 'column',
      }}
      elevation={2}
    >
      <Typography
        variant="body1"
        data-test={`general-data-card-title-${index}`}
        sx={{
          color: 'text.secondary',
        }}
      >
        {t(title)}
      </Typography>
      {isLoading ? (
        <>
          <Skeleton animation="wave" data-test={`general-data-card-skeleton-1-${index}`} />
          <Skeleton animation="wave" data-test={`general-data-card-skeleton-2-${index}`} />
        </>
      ) : (
        <>
          <Typography
            variant="h3"
            data-test={`general-data-card-value-${title}`}
            sx={{
              fontWeight: isValueAvailable ? 800 : 400,
              fontSize: 28,
              lineHeight: '32px',
              color: isValueAvailable ? 'text.primary' : 'text.disabled',
            }}
          >
            {isValueAvailable && prefix}
            {abbreviateNumericValue(value, { nullValue: 'N/A', multiplier: displayMultiplier, allowZero: true })}
            {isValueAvailable && suffix}
          </Typography>
          {isPositive && isValueAvailable && (
            <Typography
              data-test={`positive-change-${index}`}
              sx={{
                color: 'success.main',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowUpward /> {changeLabel}%
            </Typography>
          )}
          {noChange && isValueAvailable && <Box sx={{ height: 28 }} />}
          {isNegative && isValueAvailable && (
            <Typography
              data-test={`negative-change-${index}`}
              sx={{
                color: 'error.main',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowDownward /> {changeLabel}%
            </Typography>
          )}
        </>
      )}
    </Card>
  );
};
